import { useState, useEffect, useMemo } from 'react';
import { Link } from 'react-router-dom';
import {
  AlertOctagon,
  Sparkles,
  CheckCircle2,
  AlertTriangle,
  Clock,
  Search,
  Plus,
  RefreshCw,
  ExternalLink,
  Trash2,
  Edit3,
  Check,
  User,
  FolderGit2,
  ShieldAlert,
  Flame,
  CheckCircle,
  X,
  Copy,
  Wand2
} from 'lucide-react';
import { Card, CardContent } from '@/components/ui/card.js';
import { Button } from '@/components/ui/button.js';
import { Input } from '@/components/ui/input.js';
import { SkeletonCard } from '@/components/ui/skeleton.js';
import { useAuth } from '@/context/AuthContext.js';
import { useToast } from '@/context/ToastContext.js';
import { api } from '@/lib/api.js';
import { Blocker, BlockerStatus, BlockerSeverity, Project, Task } from '@/types/index.js';

function formatRelativeTime(dateString?: string | null): string {
  if (!dateString) return 'recently';
  const now = Date.now();
  const then = new Date(dateString).getTime();
  if (isNaN(then)) return dateString;
  const diffSec = Math.floor((now - then) / 1000);
  if (diffSec < 60) return 'just now';
  const diffMin = Math.floor(diffSec / 60);
  if (diffMin < 60) return `${diffMin}m ago`;
  const diffHr = Math.floor(diffMin / 60);
  if (diffHr < 24) return `${diffHr}h ago`;
  const diffDays = Math.floor(diffHr / 24);
  if (diffDays < 30) return `${diffDays}d ago`;
  return new Date(dateString).toLocaleDateString(undefined, { month: 'short', day: 'numeric' });
}

export function BlockersPage() {
  const { user } = useAuth();
  const toast = useToast();
  const role = user?.role || 'team_member';
  const isOwner = role === 'owner';
  const isManager = role === 'manager';
  const isViewer = role === 'viewer';

  const [blockers, setBlockers] = useState<Blocker[]>([]);
  const [projects, setProjects] = useState<Project[]>([]);
  const [tasks, setTasks] = useState<Task[]>([]);
  const [loading, setLoading] = useState(true);
  const [refreshing, setRefreshing] = useState(false);

  // Filters
  const [search, setSearch] = useState('');
  const [statusFilter, setStatusFilter] = useState<string>('all');
  const [projectFilter, setProjectFilter] = useState<string>('all');
  const [severityFilter, setSeverityFilter] = useState<string>('all');
  const [authorFilter, setAuthorFilter] = useState<string>('all');

  // Modal state
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [editingBlocker, setEditingBlocker] = useState<Blocker | null>(null);
  const [submitting, setSubmitting] = useState(false);

  // Quick resolve modal state
  const [resolveModalBlocker, setResolveModalBlocker] = useState<Blocker | null>(null);
  const [resolveNotesInput, setResolveNotesInput] = useState('');
  const [resolving, setResolving] = useState(false);

  // Modal form data
  const [formData, setFormData] = useState({
    project_id: '',
    task_id: '',
    blocker_description: '',
    ai_diagnosis: '',
    resolution_notes: '',
    status: 'active' as BlockerStatus,
    severity: 'high' as BlockerSeverity,
  });

  const fetchData = async (isSilent = false) => {
    if (!isSilent) setLoading(true);
    else setRefreshing(true);
    try {
      const [blockersRes, projectsRes, tasksRes] = await Promise.all([
        api.get<{ data: Blocker[] }>('/api/v1/blockers'),
        api.get<{ data: Project[] }>('/api/v1/projects'),
        api.get<{ data: Task[] }>('/api/v1/tasks'),
      ]);

      if (blockersRes?.data) setBlockers(blockersRes.data);
      if (projectsRes?.data) setProjects(projectsRes.data);
      if (tasksRes?.data) setTasks(tasksRes.data);
    } catch (err: any) {
      console.error('Failed to load blockers data:', err);
      toast.error('Failed to load blockers. Please try again.');
    } finally {
      setLoading(false);
      setRefreshing(false);
    }
  };

  useEffect(() => {
    fetchData();
  }, []);

  const openCreateModal = () => {
    setEditingBlocker(null);
    setFormData({
      project_id: projects[0]?.id || '',
      task_id: '',
      blocker_description: '',
      ai_diagnosis: '',
      resolution_notes: '',
      status: 'active',
      severity: 'high',
    });
    setIsModalOpen(true);
  };

  const openEditModal = (b: Blocker) => {
    setEditingBlocker(b);
    setFormData({
      project_id: b.project_id || '',
      task_id: b.task_id || '',
      blocker_description: b.blocker_description || '',
      ai_diagnosis: b.ai_diagnosis || '',
      resolution_notes: b.resolution_notes || '',
      status: b.status || 'active',
      severity: b.severity || 'high',
    });
    setIsModalOpen(true);
  };

  const openResolveModal = (b: Blocker) => {
    setResolveModalBlocker(b);
    setResolveNotesInput(b.resolution_notes || 'Implemented fix following AI recommendations. Issue resolved.');
  };

  const handleConfirmResolve = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!resolveModalBlocker) return;
    setResolving(true);
    try {
      await api.patch(`/api/v1/blockers/${resolveModalBlocker.id}`, {
        status: 'resolved',
        resolution_notes: resolveNotesInput.trim() || 'Resolved following AI guidance.',
      });
      toast.success('Blocker marked as successfully resolved! 🎉');
      setResolveModalBlocker(null);
      fetchData(true);
    } catch (err: any) {
      toast.error(err.message || 'Failed to resolve blocker');
    } finally {
      setResolving(false);
    }
  };

  const handleAutoSuggestAi = () => {
    if (!formData.blocker_description.trim()) {
      toast.warning('Please enter a blocker description first so AI can analyze it.');
      return;
    }
    const desc = formData.blocker_description.trim();
    let diagnosis = `AI Diagnosis: Roadblock identified in "${desc.slice(0, 60)}...". Root cause likely stems from configuration mismatch, missing dependency, or asynchronous race condition.\nRecommended Action:\n1. Inspect stack trace / server logs for specific error code.\n2. Verify environment secrets and API endpoint declarations.\n3. Implement unit / integration regression test to validate fix.`;

    if (/oauth|auth|jwt|token|login|redirect|stripe|webhook/i.test(desc)) {
      diagnosis = `AI Diagnosis: Authentication / Webhook configuration mismatch. Verify callback redirect URI matches whitelisted endpoint in provider console. Ensure webhook signing secret matches current environment.\nRecommended Action:\n1. Check URL parameters and callback domains.\n2. Verify headers (Authorization Bearer / Stripe-Signature).\n3. Re-test webhook handshake with CLI simulation.`;
    } else if (/database|sql|migration|table|query|lock/i.test(desc)) {
      diagnosis = `AI Diagnosis: Database schema, migration, or transaction deadlock. Ensure schema alterations are atomic and locks are released promptly.\nRecommended Action:\n1. Inspect active transactions and locks.\n2. Verify column data types and foreign key constraints.\n3. Apply schema migration on staging before production.`;
    } else if (/timeout|slow|memory|heap|oom|performance|v8/i.test(desc)) {
      diagnosis = `AI Diagnosis: Resource exhaustion or V8 heap limit exceeded during data processing.\nRecommended Action:\n1. Stream large datasets instead of loading entire matrix into RAM.\n2. Add pagination / query chunking limits.\n3. Benchmark memory consumption before and after transform.`;
    } else if (/dns|ssl|cert|domain|https|cors/i.test(desc)) {
      diagnosis = `AI Diagnosis: Network routing, DNS propagation, or SSL certificate handshake impediment.\nRecommended Action:\n1. Verify DNS TXT/A records and Certbot auto-renewal cron.\n2. Test TLS certificate chain with OpenSSL.\n3. Check CORS Allowed-Origins header if cross-origin.`;
    }

    setFormData((prev) => ({
      ...prev,
      ai_diagnosis: prev.ai_diagnosis ? `${prev.ai_diagnosis}\n\n${diagnosis}` : diagnosis,
    }));
    toast.info('AI Diagnostic guidance generated! 💡');
  };

  const copyAiPrompt = () => {
    if (!formData.blocker_description.trim()) {
      toast.warning('Please enter what the blocker was first.');
      return;
    }
    const prj = projects.find((p) => p.id === formData.project_id)?.name || 'Project';
    const task = tasks.find((t) => t.id === formData.task_id)?.title || '';
    const promptText = `I am facing an engineering roadblock on ${prj}${task ? ` (Task: ${task})` : ''}:\n\nBlocker: "${formData.blocker_description}"\n\nPlease diagnose the root cause, recommend step-by-step resolution, and provide the exact fix code.`;
    if (navigator?.clipboard?.writeText) {
      navigator.clipboard.writeText(promptText);
      toast.success('AI Prompt copied to clipboard! Paste into Claude/ChatGPT 📋');
    } else {
      toast.info('Prompt ready: ' + promptText.slice(0, 80) + '...');
    }
  };

  const handleDelete = async (b: Blocker) => {
    const ok = window.confirm(`Delete blocker: "${b.blocker_description.slice(0, 50)}..."?`);
    if (!ok) return;
    try {
      await api.delete(`/api/v1/blockers/${b.id}`);
      toast.info('Blocker deleted');
      fetchData(true);
    } catch (err: any) {
      toast.error(err.message || 'Failed to delete blocker');
    }
  };

  const handleFormSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!formData.blocker_description.trim()) {
      toast.warning('Please provide a description of what the blocker is.');
      return;
    }

    setSubmitting(true);
    try {
      if (editingBlocker) {
        await api.patch(`/api/v1/blockers/${editingBlocker.id}`, {
          blocker_description: formData.blocker_description.trim(),
          ai_diagnosis: formData.ai_diagnosis.trim(),
          resolution_notes: formData.resolution_notes.trim(),
          status: formData.status,
          severity: formData.severity,
        });
        toast.success('Blocker details updated successfully!');
      } else {
        await api.post('/api/v1/blockers', {
          project_id: formData.project_id || undefined,
          task_id: formData.task_id || undefined,
          blocker_description: formData.blocker_description.trim(),
          ai_diagnosis: formData.ai_diagnosis.trim(),
          resolution_notes: formData.resolution_notes.trim(),
          status: formData.status,
          severity: formData.severity,
        });
        toast.success('New blocker logged with AI guidance! 🚀');
      }
      setIsModalOpen(false);
      fetchData(true);
    } catch (err: any) {
      toast.error(err.message || 'Operation failed');
    } finally {
      setSubmitting(false);
    }
  };

  // Filtered blockers
  const filteredBlockers = useMemo(() => {
    return blockers.filter((b) => {
      // Search
      if (search.trim()) {
        const q = search.toLowerCase();
        const matchesDesc = b.blocker_description?.toLowerCase().includes(q);
        const matchesAi = b.ai_diagnosis?.toLowerCase().includes(q);
        const matchesRes = b.resolution_notes?.toLowerCase().includes(q);
        const matchesTask = b.task_title?.toLowerCase().includes(q);
        const matchesPrj = b.project_name?.toLowerCase().includes(q);
        const matchesUser = b.user_name?.toLowerCase().includes(q);
        if (!matchesDesc && !matchesAi && !matchesRes && !matchesTask && !matchesPrj && !matchesUser) {
          return false;
        }
      }
      // Status
      if (statusFilter !== 'all' && b.status !== statusFilter) return false;
      // Project
      if (projectFilter !== 'all' && b.project_id !== projectFilter) return false;
      // Severity
      if (severityFilter !== 'all' && b.severity !== severityFilter) return false;
      // Author
      if (authorFilter !== 'all' && b.user_id !== authorFilter) return false;

      return true;
    });
  }, [blockers, search, statusFilter, projectFilter, severityFilter, authorFilter]);

  // Aggregate stats
  const totalCount = blockers.length;
  const activeCount = blockers.filter((b) => b.status === 'active').length;
  const investigatingCount = blockers.filter((b) => b.status === 'investigating').length;
  const resolvedCount = blockers.filter((b) => b.status === 'resolved').length;
  const resolvedRate = totalCount > 0 ? Math.round((resolvedCount / totalCount) * 100) : 0;

  // Filter tasks for selected project in modal
  const projectTasks = useMemo(() => {
    if (!formData.project_id) return tasks;
    return tasks.filter((t) => t.project_id === formData.project_id);
  }, [tasks, formData.project_id]);

  // Unique authors
  const uniqueAuthors = useMemo(() => {
    const map = new Map<string, string>();
    blockers.forEach((b) => {
      if (b.user_id && b.user_name) map.set(b.user_id, b.user_name);
    });
    return Array.from(map.entries()).map(([id, name]) => ({ id, name }));
  }, [blockers]);

  return (
    <div className="space-y-6 pb-12">
      {/* Header Banner */}
      <div className="flex flex-col md:flex-row md:items-center justify-between gap-4 border-b border-brand-border/60 pb-5">
        <div>
          <div className="flex items-center gap-2">
            <span className="inline-flex items-center gap-1.5 px-2.5 py-0.5 rounded-full text-xs font-semibold bg-red-100 text-red-800 border border-red-200">
              <ShieldAlert className="w-3.5 h-3.5" />
              Team Resolution Engine
            </span>
            <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-xs font-medium bg-purple-100 text-purple-800 border border-purple-200">
              <Sparkles className="w-3 h-3" />
              AI Diagnosis
            </span>
          </div>
          <h1 className="text-2xl md:text-3xl font-extrabold text-brand-ink tracking-tight mt-1.5">
            Team Developer Blockers & AI Guidance
          </h1>
          <p className="text-sm text-brand-muted mt-1 max-w-3xl">
            Empower team developers to log roadblocks, document AI diagnosis prompts, and record practical next steps and resolutions.
          </p>
        </div>

        <div className="flex items-center gap-2.5">
          <Button
            variant="outline"
            size="sm"
            onClick={() => fetchData(true)}
            disabled={refreshing}
            className="border-brand-border text-brand-ink hover:bg-brand-surface"
          >
            <RefreshCw className={`w-4 h-4 mr-1.5 ${refreshing ? 'animate-spin' : ''}`} />
            Refresh
          </Button>

          {!isViewer && (
            <Button
              onClick={openCreateModal}
              className="bg-brand text-white hover:bg-brand-dark font-semibold shadow-sm"
            >
              <Plus className="w-4 h-4 mr-1.5" />
              Log New Blocker
            </Button>
          )}
        </div>
      </div>

      {/* KPI Cards */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
        <Card className="border-brand-border/80 shadow-sm bg-card hover:border-brand/40 transition">
          <CardContent className="p-4 flex items-center justify-between">
            <div>
              <p className="text-xs font-semibold text-brand-muted uppercase tracking-wider">Total Blockers</p>
              <h3 className="text-2xl font-black text-brand-ink mt-0.5">{totalCount}</h3>
              <p className="text-[11px] text-brand-muted mt-0.5">Logged across agency</p>
            </div>
            <div className="w-10 h-10 rounded-xl bg-slate-100 flex items-center justify-center text-slate-700">
              <AlertOctagon className="w-5 h-5" />
            </div>
          </CardContent>
        </Card>

        <Card className="border-red-200 bg-red-50/40 shadow-sm hover:border-red-300 transition">
          <CardContent className="p-4 flex items-center justify-between">
            <div>
              <p className="text-xs font-semibold text-red-700 uppercase tracking-wider">Active Roadblocks</p>
              <h3 className="text-2xl font-black text-red-900 mt-0.5">{activeCount}</h3>
              <p className="text-[11px] text-red-700 mt-0.5">Requiring intervention</p>
            </div>
            <div className="w-10 h-10 rounded-xl bg-red-100 flex items-center justify-center text-red-600">
              <Flame className="w-5 h-5" />
            </div>
          </CardContent>
        </Card>

        <Card className="border-blue-200 bg-blue-50/40 shadow-sm hover:border-blue-300 transition">
          <CardContent className="p-4 flex items-center justify-between">
            <div>
              <p className="text-xs font-semibold text-blue-700 uppercase tracking-wider">AI Investigation</p>
              <h3 className="text-2xl font-black text-blue-900 mt-0.5">{investigatingCount}</h3>
              <p className="text-[11px] text-blue-700 mt-0.5">Spike or testing</p>
            </div>
            <div className="w-10 h-10 rounded-xl bg-blue-100 flex items-center justify-center text-blue-600">
              <Sparkles className="w-5 h-5" />
            </div>
          </CardContent>
        </Card>

        <Card className="border-emerald-200 bg-emerald-50/40 shadow-sm hover:border-emerald-300 transition">
          <CardContent className="p-4 flex items-center justify-between">
            <div>
              <p className="text-xs font-semibold text-emerald-800 uppercase tracking-wider">Resolved</p>
              <div className="flex items-baseline gap-2 mt-0.5">
                <h3 className="text-2xl font-black text-emerald-900">{resolvedCount}</h3>
                <span className="text-xs font-bold text-emerald-700">({resolvedRate}%)</span>
              </div>
              <p className="text-[11px] text-emerald-800 mt-0.5">Solutions verified</p>
            </div>
            <div className="w-10 h-10 rounded-xl bg-emerald-100 flex items-center justify-center text-emerald-700">
              <CheckCircle className="w-5 h-5" />
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Filter and Search Bar */}
      <Card className="border-brand-border/70 shadow-sm bg-white">
        <CardContent className="p-4 space-y-3">
          <div className="flex flex-col md:flex-row items-stretch md:items-center justify-between gap-3">
            {/* Search Input */}
            <div className="relative flex-1">
              <Search className="w-4 h-4 absolute left-3 top-1/2 -translate-y-1/2 text-brand-muted" />
              <Input
                placeholder="Search blocker issue, AI advice, developer response, task or author..."
                value={search}
                onChange={(e) => setSearch(e.target.value)}
                className="pl-9 bg-brand-surface/40 border-brand-border"
              />
              {search && (
                <button
                  onClick={() => setSearch('')}
                  className="absolute right-3 top-1/2 -translate-y-1/2 text-xs text-brand-muted hover:text-brand-ink"
                >
                  Clear
                </button>
              )}
            </div>

            {/* Quick Status Tabs */}
            <div className="flex items-center gap-1 p-1 bg-slate-100 rounded-lg self-start md:self-auto overflow-x-auto text-xs">
              <button
                type="button"
                onClick={() => setStatusFilter('all')}
                className={`px-3 py-1.5 rounded-md font-semibold transition ${
                  statusFilter === 'all' ? 'bg-white text-brand-ink shadow-sm' : 'text-slate-600 hover:text-slate-900'
                }`}
              >
                All ({blockers.length})
              </button>
              <button
                type="button"
                onClick={() => setStatusFilter('active')}
                className={`px-3 py-1.5 rounded-md font-semibold transition ${
                  statusFilter === 'active' ? 'bg-red-600 text-white shadow-sm' : 'text-slate-600 hover:text-slate-900'
                }`}
              >
                Active ({activeCount})
              </button>
              <button
                type="button"
                onClick={() => setStatusFilter('investigating')}
                className={`px-3 py-1.5 rounded-md font-semibold transition ${
                  statusFilter === 'investigating' ? 'bg-blue-600 text-white shadow-sm' : 'text-slate-600 hover:text-slate-900'
                }`}
              >
                Investigating ({investigatingCount})
              </button>
              <button
                type="button"
                onClick={() => setStatusFilter('resolved')}
                className={`px-3 py-1.5 rounded-md font-semibold transition ${
                  statusFilter === 'resolved' ? 'bg-emerald-600 text-white shadow-sm' : 'text-slate-600 hover:text-slate-900'
                }`}
              >
                Resolved ({resolvedCount})
              </button>
            </div>
          </div>

          {/* Secondary Dropdown Filters */}
          <div className="grid grid-cols-2 md:grid-cols-3 gap-2.5 pt-2 border-t border-brand-border/40 text-xs">
            {/* Project Filter */}
            <div className="flex items-center gap-2">
              <label className="text-brand-muted font-medium shrink-0">Project:</label>
              <select
                value={projectFilter}
                onChange={(e) => setProjectFilter(e.target.value)}
                className="w-full text-xs border border-brand-border rounded-md px-2 py-1.5 bg-white text-brand-ink focus:outline-none focus:ring-1 focus:ring-brand"
              >
                <option value="all">All Projects ({projects.length})</option>
                {projects.map((p) => (
                  <option key={p.id} value={p.id}>
                    {p.name}
                  </option>
                ))}
              </select>
            </div>

            {/* Severity Filter */}
            <div className="flex items-center gap-2">
              <label className="text-brand-muted font-medium shrink-0">Severity:</label>
              <select
                value={severityFilter}
                onChange={(e) => setSeverityFilter(e.target.value)}
                className="w-full text-xs border border-brand-border rounded-md px-2 py-1.5 bg-white text-brand-ink focus:outline-none focus:ring-1 focus:ring-brand"
              >
                <option value="all">All Severities</option>
                <option value="critical">Critical</option>
                <option value="high">High</option>
                <option value="medium">Medium</option>
                <option value="low">Low</option>
              </select>
            </div>

            {/* Author Filter */}
            <div className="flex items-center gap-2 col-span-2 md:col-span-1">
              <label className="text-brand-muted font-medium shrink-0">Engineer:</label>
              <select
                value={authorFilter}
                onChange={(e) => setAuthorFilter(e.target.value)}
                className="w-full text-xs border border-brand-border rounded-md px-2 py-1.5 bg-white text-brand-ink focus:outline-none focus:ring-1 focus:ring-brand"
              >
                <option value="all">All Engineers</option>
                {uniqueAuthors.map((a) => (
                  <option key={a.id} value={a.id}>
                    {a.name}
                  </option>
                ))}
              </select>
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Blockers List / Feed */}
      {loading ? (
        <div className="space-y-4">
          <SkeletonCard className="h-48" />
          <SkeletonCard className="h-48" />
          <SkeletonCard className="h-48" />
        </div>
      ) : filteredBlockers.length === 0 ? (
        <Card className="border-dashed border-2 border-brand-border p-12 text-center bg-white">
          <div className="w-12 h-12 rounded-full bg-slate-100 text-slate-500 mx-auto flex items-center justify-center mb-3">
            <CheckCircle2 className="w-6 h-6 text-emerald-600" />
          </div>
          <h3 className="text-lg font-bold text-brand-ink">No Blockers Found</h3>
          <p className="text-sm text-brand-muted mt-1 max-w-md mx-auto">
            {search || statusFilter !== 'all' || projectFilter !== 'all'
              ? 'No roadblocks match the active filter criteria. Try clearing filters or search term.'
              : 'Great news! There are currently no active development roadblocks logged across the team.'}
          </p>
          {!isViewer && (
            <Button onClick={openCreateModal} className="mt-4 bg-brand text-white hover:bg-brand-dark text-xs">
              <Plus className="w-4 h-4 mr-1.5" />
              Log New Blocker
            </Button>
          )}
        </Card>
      ) : (
        <div className="space-y-4">
          {filteredBlockers.map((b) => {
            const isAuthor = b.user_id === user?.id;
            const canManage = isOwner || isManager || isAuthor;

            // Severity styling
            const severityColor =
              b.severity === 'critical'
                ? 'bg-red-100 text-red-800 border-red-200'
                : b.severity === 'high'
                ? 'bg-amber-100 text-amber-800 border-amber-200'
                : b.severity === 'medium'
                ? 'bg-blue-100 text-blue-800 border-blue-200'
                : 'bg-slate-100 text-slate-800 border-slate-200';

            // Status styling
            const statusBadge =
              b.status === 'resolved' ? (
                <span className="inline-flex items-center gap-1 px-2.5 py-0.5 rounded-full text-xs font-bold bg-emerald-100 text-emerald-800 border border-emerald-300">
                  <CheckCircle2 className="w-3.5 h-3.5 text-emerald-600" />
                  Resolved
                </span>
              ) : b.status === 'investigating' ? (
                <span className="inline-flex items-center gap-1 px-2.5 py-0.5 rounded-full text-xs font-bold bg-blue-100 text-blue-800 border border-blue-300">
                  <Sparkles className="w-3.5 h-3.5 text-blue-600 animate-pulse" />
                  AI Investigating
                </span>
              ) : (
                <span className="inline-flex items-center gap-1 px-2.5 py-0.5 rounded-full text-xs font-bold bg-red-100 text-red-800 border border-red-300">
                  <AlertTriangle className="w-3.5 h-3.5 text-red-600" />
                  Active Roadblock
                </span>
              );

            return (
              <Card
                key={b.id}
                className={`border shadow-sm transition hover:shadow-md ${
                  b.status === 'active'
                    ? 'border-red-200 bg-white hover:border-red-300'
                    : b.status === 'investigating'
                    ? 'border-blue-200 bg-white hover:border-blue-300'
                    : 'border-emerald-200/80 bg-white/90 hover:border-emerald-300'
                }`}
              >
                <CardContent className="p-5 space-y-4">
                  {/* Top Bar: Metadata & Status Badges */}
                  <div className="flex flex-wrap items-center justify-between gap-2.5 border-b border-brand-border/40 pb-3">
                    <div className="flex flex-wrap items-center gap-2">
                      {statusBadge}

                      <span className={`inline-flex items-center px-2 py-0.5 rounded text-[11px] font-bold uppercase tracking-wide border ${severityColor}`}>
                        {b.severity} priority
                      </span>

                      {b.project_name && (
                        <Link
                          to={b.project_id ? `/projects/${b.project_id}` : '/projects'}
                          className="inline-flex items-center gap-1 px-2.5 py-0.5 rounded-md text-xs font-semibold bg-brand-surface text-brand-ink hover:text-brand transition border border-brand-border"
                        >
                          <FolderGit2 className="w-3 h-3 text-brand" />
                          {b.project_name}
                        </Link>
                      )}

                      {b.task_title && (
                        <Link
                          to={b.task_id ? `/tasks/${b.task_id}` : '/tasks'}
                          className="inline-flex items-center gap-1 px-2.5 py-0.5 rounded-md text-xs font-semibold bg-slate-100 text-slate-800 hover:text-blue-700 transition border border-slate-200"
                        >
                          <ExternalLink className="w-3 h-3 text-slate-500" />
                          Task: {b.task_title}
                        </Link>
                      )}
                    </div>

                    <div className="flex items-center gap-3 text-xs text-brand-muted">
                      <div className="flex items-center gap-1">
                        <Clock className="w-3.5 h-3.5" />
                        <span>Logged {formatRelativeTime(b.created_at)}</span>
                      </div>

                      <div className="flex items-center gap-1 font-medium text-brand-ink">
                        <User className="w-3.5 h-3.5 text-brand" />
                        <span>{b.user_name}</span>
                      </div>
                    </div>
                  </div>

                  {/* 3 Pillars of Blocker Response */}
                  <div className="grid grid-cols-1 md:grid-cols-3 gap-3.5">
                    {/* Pillar 1: What was blocker */}
                    <div className="p-3.5 rounded-xl bg-red-50/60 border border-red-200 flex flex-col">
                      <div className="flex items-center gap-1.5 text-xs font-bold text-red-900 uppercase tracking-wide mb-2">
                        <AlertTriangle className="w-4 h-4 text-red-600" />
                        <span>1. What Was Blocker</span>
                      </div>
                      <p className="text-xs text-slate-800 leading-relaxed font-medium whitespace-pre-wrap flex-1">
                        {b.blocker_description}
                      </p>
                    </div>

                    {/* Pillar 2: What AI said */}
                    <div className="p-3.5 rounded-xl bg-purple-50/60 border border-purple-200 flex flex-col">
                      <div className="flex items-center justify-between text-xs font-bold text-purple-900 uppercase tracking-wide mb-2">
                        <div className="flex items-center gap-1.5">
                          <Sparkles className="w-4 h-4 text-purple-600" />
                          <span>2. What AI Said</span>
                        </div>
                        <span className="text-[10px] font-semibold bg-purple-200 text-purple-800 px-1.5 py-0.2 rounded">
                          AI Diagnosis
                        </span>
                      </div>
                      <p className="text-xs text-slate-800 leading-relaxed whitespace-pre-wrap flex-1">
                        {b.ai_diagnosis || (
                          <span className="italic text-slate-400">
                            No AI diagnosis guidance entered yet.
                          </span>
                        )}
                      </p>
                    </div>

                    {/* Pillar 3: What after that (Resolution / Next Steps) */}
                    <div className={`p-3.5 rounded-xl flex flex-col ${
                      b.status === 'resolved'
                        ? 'bg-emerald-50/70 border border-emerald-200'
                        : 'bg-amber-50/60 border border-amber-200'
                    }`}>
                      <div className="flex items-center justify-between text-xs font-bold uppercase tracking-wide mb-2">
                        <div className="flex items-center gap-1.5 text-slate-900">
                          <CheckCircle2 className={`w-4 h-4 ${b.status === 'resolved' ? 'text-emerald-600' : 'text-amber-600'}`} />
                          <span>3. What After That</span>
                        </div>
                        <span className={`text-[10px] font-semibold px-1.5 py-0.2 rounded ${
                          b.status === 'resolved'
                            ? 'bg-emerald-200 text-emerald-900'
                            : 'bg-amber-200 text-amber-900'
                        }`}>
                          {b.status === 'resolved' ? 'Resolved' : 'In Progress'}
                        </span>
                      </div>
                      <p className="text-xs text-slate-800 leading-relaxed whitespace-pre-wrap flex-1">
                        {b.resolution_notes || (
                          <span className="italic text-slate-500">
                            Action pending. Click below to document what steps were executed after AI diagnosis.
                          </span>
                        )}
                      </p>
                      {b.status === 'resolved' && b.resolved_by_name && (
                        <div className="mt-2 pt-2 border-t border-emerald-200 text-[11px] text-emerald-900 font-semibold flex items-center justify-between">
                          <span>Resolved by {b.resolved_by_name}</span>
                          {b.resolved_at && <span>{formatRelativeTime(b.resolved_at)}</span>}
                        </div>
                      )}
                    </div>
                  </div>

                  {/* Actions Toolbar */}
                  <div className="flex items-center justify-between pt-2 border-t border-brand-border/30 text-xs">
                    <span className="text-[11px] text-brand-muted font-mono">ID: {b.id}</span>

                    <div className="flex items-center gap-2">
                      {b.status !== 'resolved' && canManage && (
                        <Button
                          size="sm"
                          onClick={() => openResolveModal(b)}
                          className="bg-emerald-600 hover:bg-emerald-700 text-white font-semibold text-xs h-8"
                        >
                          <Check className="w-3.5 h-3.5 mr-1" />
                          Mark Resolved
                        </Button>
                      )}

                      {canManage && (
                        <Button
                          size="sm"
                          variant="outline"
                          onClick={() => openEditModal(b)}
                          className="border-brand-border text-brand-ink hover:bg-brand-surface font-semibold text-xs h-8"
                        >
                          <Edit3 className="w-3.5 h-3.5 mr-1 text-brand" />
                          Update / Next Steps
                        </Button>
                      )}

                      {(isOwner || isManager || isAuthor) && (
                        <Button
                          size="sm"
                          variant="ghost"
                          onClick={() => handleDelete(b)}
                          className="text-red-600 hover:bg-red-50 hover:text-red-700 h-8 px-2"
                          title="Delete blocker"
                        >
                          <Trash2 className="w-3.5 h-3.5" />
                        </Button>
                      )}
                    </div>
                  </div>
                </CardContent>
              </Card>
            );
          })}
        </div>
      )}

      {/* Log / Edit Blocker Modal */}
      {isModalOpen && (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/60 backdrop-blur-sm animate-in fade-in duration-150">
          <div className="bg-white rounded-2xl max-w-2xl w-full max-h-[90vh] flex flex-col shadow-2xl border border-brand-border overflow-hidden">
            {/* Modal Header */}
            <div className="px-6 py-4 border-b border-brand-border/60 flex items-center justify-between bg-brand-surface/30">
              <div className="flex items-center gap-2">
                <div className="w-8 h-8 rounded-lg bg-brand/10 text-brand flex items-center justify-center font-bold">
                  <ShieldAlert className="w-4 h-4" />
                </div>
                <div>
                  <h3 className="text-base font-bold text-brand-ink">
                    {editingBlocker ? 'Update Blocker & Developer Response' : 'Log New Blocker with AI Diagnosis'}
                  </h3>
                  <p className="text-xs text-brand-muted">
                    Capture the roadblock, what AI recommended, and what action was taken next.
                  </p>
                </div>
              </div>

              <button
                type="button"
                onClick={() => setIsModalOpen(false)}
                className="w-8 h-8 rounded-lg hover:bg-brand-surface flex items-center justify-center text-brand-muted hover:text-brand-ink"
              >
                <X className="w-4 h-4" />
              </button>
            </div>

            {/* Modal Body */}
            <form onSubmit={handleFormSubmit} className="flex-1 overflow-y-auto p-6 space-y-4 text-xs">
              {/* Linked Project and Task */}
              <div className="grid grid-cols-1 md:grid-cols-2 gap-3.5">
                <div>
                  <label className="block font-bold text-brand-ink mb-1">
                    Linked Project
                  </label>
                  <select
                    value={formData.project_id}
                    onChange={(e) => setFormData({ ...formData, project_id: e.target.value, task_id: '' })}
                    className="w-full border border-brand-border rounded-lg p-2.5 bg-white text-brand-ink focus:outline-none focus:ring-2 focus:ring-brand"
                  >
                    <option value="">-- General Team Blocker (No Project) --</option>
                    {projects.map((p) => (
                      <option key={p.id} value={p.id}>
                        {p.name}
                      </option>
                    ))}
                  </select>
                </div>

                <div>
                  <label className="block font-bold text-brand-ink mb-1">
                    Linked Task (Optional)
                  </label>
                  <select
                    value={formData.task_id}
                    onChange={(e) => setFormData({ ...formData, task_id: e.target.value })}
                    className="w-full border border-brand-border rounded-lg p-2.5 bg-white text-brand-ink focus:outline-none focus:ring-2 focus:ring-brand"
                  >
                    <option value="">-- No specific task --</option>
                    {projectTasks.map((t) => (
                      <option key={t.id} value={t.id}>
                        {t.title} ({t.status})
                      </option>
                    ))}
                  </select>
                </div>
              </div>

              {/* Severity and Status */}
              <div className="grid grid-cols-2 gap-3.5">
                <div>
                  <label className="block font-bold text-brand-ink mb-1">
                    Blocker Severity
                  </label>
                  <select
                    value={formData.severity}
                    onChange={(e) => setFormData({ ...formData, severity: e.target.value as BlockerSeverity })}
                    className="w-full border border-brand-border rounded-lg p-2.5 bg-white text-brand-ink focus:outline-none focus:ring-2 focus:ring-brand font-semibold"
                  >
                    <option value="critical">Critical (Blocking Sprint / Production)</option>
                    <option value="high">High (Blocking Active Task)</option>
                    <option value="medium">Medium (Impeding Flow)</option>
                    <option value="low">Low (Minor Glitch / Quirk)</option>
                  </select>
                </div>

                <div>
                  <label className="block font-bold text-brand-ink mb-1">
                    Resolution Status
                  </label>
                  <select
                    value={formData.status}
                    onChange={(e) => setFormData({ ...formData, status: e.target.value as BlockerStatus })}
                    className="w-full border border-brand-border rounded-lg p-2.5 bg-white text-brand-ink focus:outline-none focus:ring-2 focus:ring-brand font-semibold"
                  >
                    <option value="active">Active Roadblock</option>
                    <option value="investigating">Under AI Investigation</option>
                    <option value="resolved">Resolved</option>
                  </select>
                </div>
              </div>

              {/* Pillar 1: What was blocker */}
              <div className="space-y-1">
                <label className="block font-bold text-red-900 flex items-center gap-1.5">
                  <AlertTriangle className="w-3.5 h-3.5 text-red-600" />
                  1. What Was Blocker? (Required)
                </label>
                <textarea
                  rows={3}
                  required
                  placeholder="Describe the roadblock, bug, API 400 error, missing credentials, or memory crash..."
                  value={formData.blocker_description}
                  onChange={(e) => setFormData({ ...formData, blocker_description: e.target.value })}
                  className="w-full border border-red-200 bg-red-50/20 rounded-lg p-3 text-brand-ink focus:outline-none focus:ring-2 focus:ring-red-500 font-sans leading-relaxed"
                />
              </div>

              {/* Pillar 2: What AI said */}
              <div className="space-y-1">
                <div className="flex items-center justify-between">
                  <label className="block font-bold text-purple-900 flex items-center gap-1.5">
                    <Sparkles className="w-3.5 h-3.5 text-purple-600" />
                    2. What Did AI Say? (AI Advice / Diagnosis)
                  </label>
                  <div className="flex items-center gap-1.5">
                    <button
                      type="button"
                      onClick={handleAutoSuggestAi}
                      className="inline-flex items-center gap-1 text-[11px] font-semibold text-purple-700 hover:text-purple-950 bg-purple-100 hover:bg-purple-200 px-2 py-0.5 rounded transition"
                      title="Generate diagnostic advice based on your blocker description"
                    >
                      <Wand2 className="w-3 h-3" />
                      Auto-Diagnose
                    </button>
                    <button
                      type="button"
                      onClick={copyAiPrompt}
                      className="inline-flex items-center gap-1 text-[11px] font-semibold text-slate-700 hover:text-slate-900 bg-slate-100 hover:bg-slate-200 px-2 py-0.5 rounded transition"
                      title="Copy structured debug prompt to paste into Claude/ChatGPT"
                    >
                      <Copy className="w-3 h-3" />
                      Copy Prompt
                    </button>
                  </div>
                </div>
                <textarea
                  rows={3}
                  placeholder="Paste the prompt you ran or summarize what AI diagnosed, recommended, or code solution suggested..."
                  value={formData.ai_diagnosis}
                  onChange={(e) => setFormData({ ...formData, ai_diagnosis: e.target.value })}
                  className="w-full border border-purple-200 bg-purple-50/20 rounded-lg p-3 text-brand-ink focus:outline-none focus:ring-2 focus:ring-purple-500 font-sans leading-relaxed"
                />
              </div>

              {/* Pillar 3: What after that */}
              <div className="space-y-1">
                <div className="flex items-center justify-between">
                  <label className="block font-bold text-emerald-900 flex items-center gap-1.5">
                    <CheckCircle2 className="w-3.5 h-3.5 text-emerald-600" />
                    3. What After That? (Action Taken / Developer Response / Resolution)
                  </label>
                  <div className="flex items-center gap-1 text-[10px] text-brand-muted">
                    Quick-fill:
                    <button
                      type="button"
                      onClick={() => setFormData({ ...formData, resolution_notes: 'Applied AI fix recommendations and verified automated tests pass 100%.' })}
                      className="underline hover:text-emerald-800"
                    >
                      Fix applied
                    </button>
                    <span>•</span>
                    <button
                      type="button"
                      onClick={() => setFormData({ ...formData, resolution_notes: 'Updated configuration & redeployed staging environment.' })}
                      className="underline hover:text-emerald-800"
                    >
                      Config updated
                    </button>
                  </div>
                </div>
                <textarea
                  rows={3}
                  placeholder="What steps did you take next? Did you apply the AI solution, update configs, deploy a fix, or verify tests?..."
                  value={formData.resolution_notes}
                  onChange={(e) => setFormData({ ...formData, resolution_notes: e.target.value })}
                  className="w-full border border-emerald-200 bg-emerald-50/20 rounded-lg p-3 text-brand-ink focus:outline-none focus:ring-2 focus:ring-emerald-500 font-sans leading-relaxed"
                />
              </div>

              {/* Modal Footer */}
              <div className="pt-4 border-t border-brand-border/60 flex items-center justify-end gap-2.5">
                <Button
                  type="button"
                  variant="outline"
                  onClick={() => setIsModalOpen(false)}
                  className="border-brand-border"
                >
                  Cancel
                </Button>
                <Button
                  type="submit"
                  disabled={submitting}
                  className="bg-brand text-white hover:bg-brand-dark font-semibold shadow-sm"
                >
                  {submitting ? 'Saving...' : editingBlocker ? 'Update Blocker & Response' : 'Save Blocker Entry'}
                </Button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* Quick Resolve Modal (replaces window.prompt) */}
      {resolveModalBlocker && (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/60 backdrop-blur-sm animate-in fade-in duration-150">
          <div className="bg-white rounded-2xl max-w-lg w-full shadow-2xl border border-brand-border overflow-hidden">
            <div className="px-6 py-4 border-b border-brand-border/60 flex items-center justify-between bg-emerald-50/40">
              <div className="flex items-center gap-2">
                <div className="w-8 h-8 rounded-lg bg-emerald-100 text-emerald-700 flex items-center justify-center font-bold">
                  <CheckCircle2 className="w-5 h-5" />
                </div>
                <div>
                  <h3 className="text-base font-bold text-slate-900">Resolve Blocker & Record Action</h3>
                  <p className="text-xs text-brand-muted">Record the developer action taken after AI guidance</p>
                </div>
              </div>
              <button
                type="button"
                onClick={() => setResolveModalBlocker(null)}
                className="w-8 h-8 rounded-lg hover:bg-slate-100 flex items-center justify-center text-slate-500 hover:text-slate-800"
              >
                <X className="w-4 h-4" />
              </button>
            </div>

            <form onSubmit={handleConfirmResolve} className="p-6 space-y-4 text-xs">
              {/* Blocker & AI recap */}
              <div className="p-3 rounded-lg bg-slate-50 border border-slate-200 space-y-1.5">
                <p className="font-bold text-slate-800 text-[11px] uppercase tracking-wide">Blocker Summary:</p>
                <p className="text-slate-700 text-xs italic line-clamp-2">"{resolveModalBlocker.blocker_description}"</p>
                {resolveModalBlocker.ai_diagnosis && (
                  <div className="pt-1.5 border-t border-slate-200 flex items-start gap-1 text-[11px] text-purple-900">
                    <Sparkles className="w-3.5 h-3.5 text-purple-600 shrink-0 mt-0.5" />
                    <span className="line-clamp-2"><strong>AI Advice:</strong> {resolveModalBlocker.ai_diagnosis}</span>
                  </div>
                )}
              </div>

              {/* Pillar 3 Input: What After That */}
              <div className="space-y-1.5">
                <div className="flex items-center justify-between">
                  <label className="font-bold text-emerald-950 flex items-center gap-1.5">
                    <Check className="w-3.5 h-3.5 text-emerald-600" />
                    What After That? (Action Taken / Resolution Notes)
                  </label>
                  <span className="text-[10px] text-brand-muted">Developer response</span>
                </div>
                <textarea
                  rows={4}
                  required
                  placeholder="Describe what was done after AI diagnosis (e.g. fix implemented, tests verified, PR merged)..."
                  value={resolveNotesInput}
                  onChange={(e) => setResolveNotesInput(e.target.value)}
                  className="w-full border border-emerald-300 bg-emerald-50/20 rounded-lg p-3 text-brand-ink focus:outline-none focus:ring-2 focus:ring-emerald-500 leading-relaxed font-sans"
                />
                {/* Quick suggestions */}
                <div className="flex flex-wrap items-center gap-1.5 pt-1">
                  <span className="text-[10px] text-slate-500">Quick-fill:</span>
                  <button
                    type="button"
                    onClick={() => setResolveNotesInput('Applied fix recommended by AI. All local & automated CI tests pass.')}
                    className="text-[10px] bg-slate-100 hover:bg-slate-200 text-slate-700 px-2 py-0.5 rounded transition"
                  >
                    AI Fix Applied & Tested
                  </button>
                  <button
                    type="button"
                    onClick={() => setResolveNotesInput('Updated server configuration, redeployed to staging, and verified live handshake.')}
                    className="text-[10px] bg-slate-100 hover:bg-slate-200 text-slate-700 px-2 py-0.5 rounded transition"
                  >
                    Config Deployed
                  </button>
                </div>
              </div>

              <div className="pt-3 border-t border-slate-200 flex items-center justify-end gap-2">
                <Button
                  type="button"
                  variant="outline"
                  onClick={() => setResolveModalBlocker(null)}
                  className="border-slate-300 text-slate-700"
                >
                  Cancel
                </Button>
                <Button
                  type="submit"
                  disabled={resolving || !resolveNotesInput.trim()}
                  className="bg-emerald-600 hover:bg-emerald-700 text-white font-semibold shadow-sm"
                >
                  <CheckCircle2 className="w-4 h-4 mr-1.5" />
                  {resolving ? 'Resolving...' : 'Confirm & Mark Resolved'}
                </Button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
}
export default BlockersPage;
